import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, throwError, combineLatest } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { DeviceAuthenticationStatusService } from '@shure/shared/angular/data-access/device-authentication';
import {
	NodeChangeType,
	ApolloQueryErrorMapper,
	ApolloQueryError,
	SubscriptionManagerService,
	CombiningSubscriptionManager,
	waitForGQLCompleteQueryOrThrow
} from '@shure/shared/angular/data-access/system-api/core';
import { Response, ResponseState } from '@shure/shared/angular/data-access/system-api/models';
import { DeviceReadonlyStateProvider } from '@shure/shared/angular/utils/device-readonly';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { noMoreSubscribers, tapOnce } from '@shure/shared/typescript/rxjs';

import { CertValidationResponse, Settings8021xApiService, Sh8021xConfig, Sh8021xSettingsDevice } from '../api';

import {
	RebootDeviceMutationGQL,
	Reset8021XMutationGQL,
	Security8021XSettingsDeviceQueryGQL,
	Security8021XSettingsDeviceSubscriptionGQL,
	Set8021XConfigMutationGQL,
	Set8021XEnabledMutationGQL,
	ValidateCertificate8021XMutationGQL
} from './graphql/generated/8021x.generated';
import { map8021XCertificateFromSysApi, map8021XConfigToSysApi, map8021xSettingsDeviceFromSysApi } from './mappers';

@Injectable()
export class SysApi8021xApiService implements Settings8021xApiService {
	private readonly logger: ILogger;

	private readonly security8021XSettingsSubscriptionManager: CombiningSubscriptionManager;

	constructor(
		logger: ILogger,
		private readonly security8021XSettingsDeviceQueryGQL: Security8021XSettingsDeviceQueryGQL,
		private readonly set8021XConfigMutationGQL: Set8021XConfigMutationGQL,
		private readonly validateCertificateMutationGQL: ValidateCertificate8021XMutationGQL,
		private readonly set8021XEnabledMutationGQL: Set8021XEnabledMutationGQL,
		private readonly reset8021XMutationGQL: Reset8021XMutationGQL,
		private readonly rebootDeviceMutationGQL: RebootDeviceMutationGQL,
		security8021XSettingsSubscriptionGQL: Security8021XSettingsDeviceSubscriptionGQL,
		private readonly deviceAuthenticationStatusService: DeviceAuthenticationStatusService,
		private readonly readonlyStateProvider: DeviceReadonlyStateProvider,
		subscriptionManagerService: SubscriptionManagerService
	) {
		this.logger = logger.createScopedLogger('SysApi8021xApiService');

		this.security8021XSettingsSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'SysApi8021xApiService',
				changeTypes: [
					NodeChangeType.DeviceSecurity_8021X,
					NodeChangeType.DeviceAuthentication,
					NodeChangeType.DeviceNetworkSwitchConfiguration
				],
				subscriptionGql: security8021XSettingsSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}

	public getDevice$(deviceId: string): Observable<Sh8021xSettingsDevice> {
		const queryId = uuidv4();
		this.logger.trace('getDevice$()', 'Querying device 802.1x settings', {
			deviceId,
			queryId
		});

		return combineLatest([
			this.security8021XSettingsDeviceQueryGQL
				.watch({ variables: { nodeId: deviceId }, errorPolicy: 'all' })
				.valueChanges.pipe(waitForGQLCompleteQueryOrThrow()),
			this.deviceAuthenticationStatusService.getDeviceState$(deviceId),
			this.readonlyStateProvider.isReadonly$(deviceId)
		]).pipe(
			map(([query, auth, isReadonly]) => {
				if (query.data?.node && 'features' in query.data.node) {
					const device = map8021xSettingsDeviceFromSysApi(
						query.data.node as Parameters<typeof map8021xSettingsDeviceFromSysApi>[0],
						auth.authenticated,
						isReadonly
					);
					this.logger.trace('getDevice$()', 'Done', { deviceId });
					return device;
				}
				this.logger.error('getDevice$()', 'Failed to query audio encryption settings device', { deviceId });
				throw ApolloQueryErrorMapper.getError(query);
			}),
			tapOnce((device) => {
				this.security8021XSettingsSubscriptionManager.subscribe(queryId, device.id);
			}),
			noMoreSubscribers(() => {
				this.logger.trace('security8021XSettingsSubscriptionManager.onSubscribers.noMoreSubscribers', '', {
					deviceId,
					queryId
				});
				this.security8021XSettingsSubscriptionManager.unsubscribeQuery(queryId);
			}),
			catchError((error: Error) => {
				this.logger.error('getDevice$()', 'Failed to query 8021x settings device', { deviceId, error });
				return throwError(() => new ApolloQueryError(error));
			})
		);
	}

	public set8021xConfig(
		deviceId: string,
		config: Sh8021xConfig,
		_isCurrentlyEnabled: boolean
	): Observable<Response<void, string>> {
		this.logger.trace('set8021xConfig()', 'updating device 802.1x config', { deviceId });
		return this.set8021XConfigMutationGQL
			.mutate({
				variables: {
					nodeId: deviceId,
					config: map8021XConfigToSysApi(config),
					injectKeyring: '//supplied-by-keyring-injector'
				},
				errorPolicy: 'all'
			})
			.pipe(
				map((result) => {
					if (result.data?.configure8021x.error) {
						this.logger.error('set8021xConfig()', 'error', result.data.configure8021x.error);
						throw new Error(result.data.configure8021x.error.message);
					}
					this.logger.trace('set8021xConfig()', 'Done', { deviceId });
					return { state: ResponseState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('set8021xConfig', 'Error', { error });
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}

	public validateCertificate(certificate: string): Observable<Response<CertValidationResponse, string>> {
		this.logger.trace('validateCertificate()', 'validating certificate');
		return this.validateCertificateMutationGQL
			.mutate({
				variables: {
					certificate
				},
				errorPolicy: 'all'
			})
			.pipe(
				map((result) => {
					this.logger.trace('validateCertificate()', 'Done');
					if (result.data?.validateCertificate8021X.certificateInfo) {
						return {
							state: ResponseState.Done,
							done: {
								certificateInfo: map8021XCertificateFromSysApi(
									result.data.validateCertificate8021X.certificateInfo
								),
								error: result.data?.validateCertificate8021X.error?.message
							}
						};
					}
					throw new Error(result.data?.validateCertificate8021X.error?.message);
				}),
				catchError((error: Error) => {
					this.logger.error('validateCertificate', 'Error', { error });
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}

	public set8021xEnabled(deviceId: string, enable: boolean, rebootNow: boolean): Observable<Response<void, string>> {
		this.logger.trace('Set8021XEnabled()', 'toggling 802.1x', { deviceId, enable, rebootNow });
		return this.set8021XEnabledMutationGQL
			.mutate({
				variables: {
					nodeId: deviceId,
					enable,
					rebootNow,
					injectKeyring: '//supplied-by-keyring-injector'
				},
				errorPolicy: 'all'
			})
			.pipe(
				map((result) => {
					if (result.data?.enable8021x.error) {
						this.logger.error('Set8021XEnabled()', 'error', result.data.enable8021x.error);
						throw new Error(result.data.enable8021x.error.message);
					}
					this.logger.trace('Set8021XEnabled()', 'Done', { deviceId });
					return { state: ResponseState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('Set8021XEnabled()', 'Error', { error });
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}

	public reset8021xConfig(deviceId: string, rebootNow: boolean): Observable<Response<void, string>> {
		this.logger.trace('reset8021xConfig()', 'resetting 802.1x config', { deviceId, rebootNow });
		return this.reset8021XMutationGQL
			.mutate({
				variables: {
					nodeId: deviceId,
					rebootNow,
					injectKeyring: '//supplied-by-keyring-injector'
				},
				errorPolicy: 'all'
			})
			.pipe(
				map((result) => {
					if (result.data?.reset8021x.error) {
						this.logger.error('reset8021xConfig()', 'error', result.data.reset8021x.error);
						throw new Error(result.data.reset8021x.error.message);
					}
					this.logger.trace('reset8021xConfig()', 'Done', { deviceId });
					return { state: ResponseState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('reset8021xConfig()', 'Error', { error });
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}

	public rebootDevice(deviceId: string): Observable<Response<void, string>> {
		this.logger.trace('rebootDevice()', 'Rebooting device', { deviceId });
		return this.rebootDeviceMutationGQL
			.mutate({
				variables: { rebootDeviceId: deviceId, injectKeyring: '//supplied-by-keyring-injector' },
				errorPolicy: 'all'
			})
			.pipe(
				map((result) => {
					if (result.data?.rebootDevice.error) {
						this.logger.error('rebootDevice', 'error', result.data?.rebootDevice.error);
						throw new Error(result.data?.rebootDevice.error.message);
					}
					this.logger.trace('rebootDevice', 'Done', { deviceId });
					return { state: ResponseState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('rebootDevice', 'Error', { error });
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}
}
